import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { useCreateAPIKeyMutation } from "@gram/client/react-query/createAPIKey";
import { useMarketplaceSettings } from "@gram/client/react-query/marketplaceSettings";
import { usePublishStatus } from "@gram/client/react-query/publishStatus";
import { useProjectSlugForRequests } from "@/contexts/Sdk";
import { useOrgRoutes } from "@/routes";
import type { AgentPlatform, PlatformSetupStep } from "../types";

const API_KEY_PLACEHOLDER = "{{GRAM_API_KEY}}";
const PROJECT_SLUG_PLACEHOLDER = "{{GRAM_PROJECT_SLUG}}";
const MARKETPLACE_URL_PLACEHOLDER = "{{GRAM_MARKETPLACE_URL}}";
const REPO_URL_PLACEHOLDER = "{{GRAM_REPO_URL}}";
const REPO_NAME_PLACEHOLDER = "{{GRAM_REPO_NAME}}";
const REPO_OWNER_PLACEHOLDER = "{{GRAM_REPO_OWNER}}";
const CODEX_PLUGIN_NAME_PLACEHOLDER = "{{GRAM_CODEX_PLUGIN_NAME}}";
const CLAUDE_PLUGIN_NAME_PLACEHOLDER = "{{GRAM_CLAUDE_PLUGIN_NAME}}";
const CURSOR_PLUGIN_NAME_PLACEHOLDER = "{{GRAM_CURSOR_PLUGIN_NAME}}";
const MARKETPLACE_NAME_PLACEHOLDER = "{{GRAM_MARKETPLACE_NAME}}";
const DEVICE_AGENT_URL_PLACEHOLDER = "{{GRAM_DEVICE_AGENT_URL}}";

function applySubstitutions(
  input: string,
  substitutions: Array<[string, string]>,
): string {
  let out = input;
  for (const [marker, value] of substitutions) {
    out = out.split(marker).join(value);
  }
  return out;
}

export interface PlatformPlaceholders {
  /**
   * The step's snippet with every placeholder filled, or undefined while a
   * value it needs is still missing.
   */
  snippetFor: (step: PlatformSetupStep, apiKey?: string) => string | undefined;
  /** Fills a help link's URL. Never carries the API key — see below. */
  linkFor: (url: string) => string;
}

/** The org- and project-scoped values a platform's instructions interpolate. */
export function usePlatformPlaceholders(): PlatformPlaceholders {
  const { data: publishStatus } = usePublishStatus(undefined, undefined, {
    throwOnError: false,
  });
  const { data: marketplaceSettings } = useMarketplaceSettings(
    undefined,
    undefined,
    { throwOnError: false },
  );
  const projectSlug = useProjectSlugForRequests();
  const deviceAgentUrl = useOrgRoutes().deviceAgent.href();
  // The marketplace.json "name" field: the `extraKnownMarketplaces` key and the
  // `<plugin>@<marketplace>` suffix in `enabledPlugins`. Sourced from the server
  // (naming.ResolveMarketplaceName: the override, else the frozen name the
  // project last published under, else the org-slug-normalized, project-scoped
  // computed name) rather than re-derived here, which previously hardcoded the
  // wrong "-gram" suffix instead of "-speakeasy" and ignored non-default-project
  // scoping entirely.
  const marketplaceName = marketplaceSettings?.effectiveName ?? "";
  // The plugin slugs come from the server for the same reason the marketplace
  // name does: it derives them from the name the plugins were published under
  // (naming.PublishedHooksOrgName), not the name the organization has now. An
  // org that renamed after publishing still has acme-observability in its
  // repo, and a slug rebuilt from the current slug would name a plugin that is
  // not there — which Claude and Cursor accept in silence, enabling nothing.
  const marketplaceUrl = publishStatus?.marketplaceUrl ?? "";

  // The API key is a live secret and must never be interpolated into a URL —
  // an href leaks it via the address bar, Referer header, browser history, and
  // server logs. So it is absent here; only snippetFor adds it.
  const substitutions: Array<[string, string]> = [
    [PROJECT_SLUG_PLACEHOLDER, projectSlug],
    [MARKETPLACE_URL_PLACEHOLDER, marketplaceUrl],
    [REPO_URL_PLACEHOLDER, publishStatus?.repoUrl ?? ""],
    [REPO_NAME_PLACEHOLDER, publishStatus?.repoName ?? ""],
    [REPO_OWNER_PLACEHOLDER, publishStatus?.repoOwner ?? ""],
    [
      CODEX_PLUGIN_NAME_PLACEHOLDER,
      publishStatus?.codexObservabilityPlugin ?? "",
    ],
    [
      CLAUDE_PLUGIN_NAME_PLACEHOLDER,
      publishStatus?.claudeObservabilityPlugin ?? "",
    ],
    [
      CURSOR_PLUGIN_NAME_PLACEHOLDER,
      publishStatus?.cursorObservabilityPlugin ?? "",
    ],
    [MARKETPLACE_NAME_PLACEHOLDER, marketplaceName],
    [DEVICE_AGENT_URL_PLACEHOLDER, deviceAgentUrl],
  ];

  return {
    snippetFor: (step, apiKey) => {
      if (!step.code) return undefined;
      if (step.requiresApiKey && !apiKey) return undefined;
      // Withhold the snippet while any value it interpolates is missing,
      // rather than naming the ones that happen to need it today. A read that
      // failed leaves the repo and marketplace fields empty exactly as an
      // unresolved one does, and a snippet carrying "" or "/" is worse than no
      // snippet: it looks copyable and configures nothing.
      const code = step.code;
      // Validate before inserting into a quoted shell assignment: a quote in a
      // server value could bypass the generated command's runtime slug check.
      if (
        code.includes(CURSOR_PLUGIN_NAME_PLACEHOLDER) &&
        !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(
          publishStatus?.cursorObservabilityPlugin ?? "",
        )
      ) {
        return undefined;
      }
      if (
        substitutions.some(([marker, value]) => !value && code.includes(marker))
      ) {
        return undefined;
      }
      return applySubstitutions(step.code, [
        ...substitutions,
        [API_KEY_PLACEHOLDER, apiKey ?? ""],
      ]);
    },
    linkFor: (url) => applySubstitutions(url, substitutions),
  };
}

export interface PlatformApiKeys {
  keys: Record<string, string>;
  pending: Record<string, boolean>;
  errors: Record<string, string>;
  ensure: (platform: AgentPlatform) => void;
}

/** Mints the hooks-scoped API key a platform's snippets need, once each. */
export function usePlatformApiKeys({
  shareAnthropicKey = false,
}: { shareAnthropicKey?: boolean } = {}): PlatformApiKeys {
  const [keys, setKeys] = useState<Record<string, string>>({});
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const createKeyMutation = useCreateAPIKeyMutation();
  // Reserve synchronously: sibling effects can ensure before React renders.
  // Successful reservations stay locked; failures release them for retry.
  const reserved = useRef(new Set<string>());

  const ensure = useCallback(
    (platform: AgentPlatform) => {
      const needsKey = platform.setupSteps.some((s) => s.requiresApiKey);
      if (!needsKey) return;
      const shared =
        shareAnthropicKey &&
        (platform.id === "claude" || platform.id === "claude-cowork");
      const ids = shared ? ["claude", "claude-cowork"] : [platform.id];
      const keyId = ids[0]!;
      if (reserved.current.has(keyId)) return;
      reserved.current.add(keyId);
      const values = <T>(value: T): Record<string, T> =>
        Object.fromEntries(ids.map((id) => [id, value]));

      setPending((prev) => ({ ...prev, ...values(true) }));
      setErrors((prev) => {
        const next = { ...prev };
        for (const id of ids) delete next[id];
        return next;
      });

      // The api_keys table enforces UNIQUE (organization_id, name) on
      // non-deleted rows, and key tokens are only returned on creation (never
      // re-readable via listKeys). Both constraints mean we can't "fetch the
      // existing key" — we have to mint a fresh one with a unique name on each
      // run. The timestamp suffix tells admins which run produced each entry
      // in the API Keys list; a random suffix guarantees uniqueness even when
      // two tabs/remounts mint a key in the same second.
      const timestamp = new Date().toISOString().slice(0, 19).replace("T", " ");
      const uniqueSuffix = Math.random().toString(36).slice(2, 6);
      createKeyMutation.mutate(
        {
          security: { sessionHeaderGramSession: "" },
          request: {
            createKeyForm: {
              name: `${shared ? "Claude Code and Cowork" : platform.name} hooks (setup ${timestamp} ${uniqueSuffix})`,
              scopes: ["hooks"],
            },
          },
        },
        {
          onSuccess: (data) => {
            setPending((prev) => ({ ...prev, ...values(false) }));
            if (data.key) {
              setKeys((prev) => ({ ...prev, ...values(data.key!) }));
            } else {
              reserved.current.delete(keyId);
              setErrors((prev) => ({
                ...prev,
                ...values("API key token missing from response."),
              }));
            }
          },
          onError: (err) => {
            reserved.current.delete(keyId);
            setPending((prev) => ({ ...prev, ...values(false) }));
            const msg =
              err instanceof Error
                ? err.message
                : "Failed to generate API key.";
            setErrors((prev) => ({ ...prev, ...values(msg) }));
            toast.error(`Failed to generate API key: ${msg}`);
          },
        },
      );
    },
    [shareAnthropicKey, createKeyMutation],
  );

  return { keys, pending, errors, ensure };
}
